/**
 * Dependencies.
 */
import Moment from 'moment-timezone';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

import AddressModel from '@/api/address/address.model';
import StatusModel from '@/api/status/status.model';
import { formatAddressSingleLine } from '@/modules/main/utils/address';
import {
  ORDER_REQUEST_STATUS,
  ORDER_REQUEST_TYPES
} from '@/modules/tracking/constants';

/**
 * Interface `IHistory`.
 */
export interface IHistory {
  hideHistory?: boolean;

  historyType: 'request' | 'tracking';

  request?: {
    from?: { address?: AddressModel; date?: string };

    id?: number;

    isInternal?: boolean;

    to?: { address?: AddressModel; date?: string };

    type: string;
  };

  status: StatusModel;

  statusDate: string;

  tracking?: {
    message: string;
  };
}

/**
 * Component.
 */
@Component({
  name: 'history'
})
export default class extends Vue {
  /**
   * Property `moment`.
   */
  moment = Moment;

  /**
   * Property `ORDER_REQUEST_STATUS`.
   */
  ORDER_REQUEST_STATUS = ORDER_REQUEST_STATUS;

  /**
   * Property `ORDER_REQUEST_TYPES`.
   */
  ORDER_REQUEST_TYPES = ORDER_REQUEST_TYPES;

  /**
   * Property `history`.
   */
  @Prop({ type: Array })
  history: IHistory[];

  /**
   * Getter `key`.
   */
  get key() {
    const { key = null } = this.$route.params || {};

    return key;
  }

  /**
   * Getter history items.
   */
  get historyItems() {
    const items = this.history.map(item => {
      const {
        hideHistory = false,
        historyType = null,
        request = null,
        status = null,
        statusDate = null,
        tracking = null
      } = item || {};
      const { code: statusCode, name: statusName } = status || {};

      return {
        hideHistory,
        historyType,
        request,
        statusCode,
        statusDate,
        statusName,
        tracking
      };
    });

    const filteredItems = items.filter(({ hideHistory, request }) => {
      const { type } = request || {};
      return !hideHistory && type !== ORDER_REQUEST_TYPES.MESSAGE;
    });

    return filteredItems;
  }

  /**
   * Handle history date.
   */
  handleHistoryDate(statusDate: string) {
    return statusDate ? this.moment(statusDate).format('DD MMM YYYY') : '';
  }

  /**
   * Handle history hour.
   */
  handleHistoryHour(statusDate: string) {
    return statusDate ? this.moment(statusDate).format('HH:mm') : '';
  }

  /**
   * Handle is change request.
   */
  handleIsChangeRequest(type: string) {
    return [
      ORDER_REQUEST_TYPES.CHANGE_ADDRESS,
      ORDER_REQUEST_TYPES.CHANGE_DATE,
      ORDER_REQUEST_TYPES.CHANGE_DATE_AND_ADDRESS
    ].includes(type);
  }

  /**
   * Handle build request detail.
   */
  handleBuildRequestDetail({
    detail = null,
    showAdditionalInformation = false,
    type = null
  }: {
    detail: { address?: AddressModel; date?: string };
    showAdditionalInformation?: boolean;
    type: string;
  }) {
    let dateString = '';
    let addressString = '';

    if (
      [
        ORDER_REQUEST_TYPES.CHANGE_DATE,
        ORDER_REQUEST_TYPES.CHANGE_DATE_AND_ADDRESS
      ].includes(type)
    ) {
      const { date = null } = detail || {};

      dateString = this.handleHistoryDate(date);
    }

    if (
      [
        ORDER_REQUEST_TYPES.CHANGE_ADDRESS,
        ORDER_REQUEST_TYPES.CHANGE_DATE_AND_ADDRESS
      ].includes(type)
    ) {
      const { address = null } = detail || {};

      addressString = formatAddressSingleLine({
        address,
        showAdditionalInformation
      });
    }

    return [dateString, addressString].filter(Boolean).join(' | ');
  }

  /**
   * Handle view request chat messages.
   */
  handleViewRequestChatMessages() {
    this.$router.push({
      name: 'RequestMessageChat',
      params: {
        key: this.key
      }
    });
  }
}
